import { useState, useEffect } from 'react'
import { Search2Icon } from '@chakra-ui/icons'
import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  RadioGroup,
  Radio
} from '@chakra-ui/react'
import { Input, InputGroup, InputLeftElement } from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { toast } from 'react-toastify'
import { FormGroup, Label } from 'reactstrap'
import { DropdownItem, DropdownMenu, DropdownToggle, Dropdown } from 'reactstrap'
import { primary } from 'theme/globalStyles'
import { UpdateBody } from 'types/common'
import { createStep } from 'API/step'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { StepPosition, StepPositionNames, StepNotify } from 'constants/processStep'
import { EIconDefaultId } from 'interfaces/iconBuilder'
import { IStep } from 'interfaces/step'
import { ITheme } from 'interfaces/theme'
import { getValidArray } from 'utils/common'
import { getRenderProcess } from '../../../../../../utils'
import SelectStepPanel from './components/SelectStepPanel'
import styles from './modalAddStep.module.scss'

interface IModalAddStepProps {
  isOpen: boolean
  procedureId: number
  handleShowAutosave: () => void
  onClose: () => void
}

enum ECreateMode {
  CREATE = 'create',
  EXISTING = 'existing'
}

const ModalAddStep = ({ isOpen, procedureId, handleShowAutosave, onClose }: IModalAddStepProps) => {
  const { processStore, commonLibraryStore, organizationStore } = useStores()
  const [stepPosition, setStepPosition] = useState<number>(0)
  const currentSteps = processStore?.process?.steps ?? []
  const currentStepIds = currentSteps.map(step => step.id)
  const selectedCommonStepIds = currentSteps.map(step => step.originalStepId)
  const stepIdsToExclude = [...currentStepIds, ...selectedCommonStepIds]
  const [selectedCommonStepId, setSelectedCommonStepId] = useState('0')
  const [createMode, setCreateMode] = useState<ECreateMode>(ECreateMode.CREATE)
  const [keyword, setKeyword] = useState('')
  const [stepPositionName, setStepPositionName] = useState<string>('')
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [newTitle, setNewTitle] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const toggle = () => setDropdownOpen(prevState => !prevState)
  const { organization } = organizationStore
  const currentTheme: ITheme = organization?.theme ?? {}

  useEffect(() => {
    setStepPosition(StepPosition.AppendToTheEndOfList)
    setStepPositionName(StepPositionNames.AppendToTheEndOfList)
    setNewTitle('')
  }, [isOpen])

  async function handleCreateStep() {
    setLoading(true)
    if (stepPosition) {
      try {
        const body: UpdateBody<IStep> = {
          name: newTitle ?? '',
          processId: procedureId,
          layoutId: 1,
          addOption: stepPosition,
          selectedPosition: 1,
          archived: false,
          iconId: EIconDefaultId.STEP
        }
        if (createMode === ECreateMode.EXISTING) {
          body.originalStepId = Number(selectedCommonStepId)
        }
        await createStep(body)
        toast.success(StepNotify.SUCCESS)
        await getRenderProcess(procedureId, processStore)
        await handleShowAutosave()
      } catch (error: any) {
        toast.error(StepNotify.FALSE)
      }
      onClose()
    } else {
      toast.warning(StepNotify.WARNING)
    }
    setLoading(false)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent className={styles.addDialog}>
        <ModalHeader
          fontWeight="500"
          fontSize="18px"
          lineHeight="28px"
          color="gray.800"
          borderBottom="1px solid #E2E8F0"
        >
          Add New Step
        </ModalHeader>
        <ModalCloseButton
          boxShadow="unset"
          border="unset"
          background="#fff"
          _focus={{ borderColor: 'unset' }}
          _active={{ background: '#fff', borderColor: 'unset' }}
        />
        <ModalBody paddingTop="24px" paddingBottom="27px">
          <FormGroup>
            <RadioGroup
              flexDirection="row"
              display="flex"
              color="gray.700"
              fontSize="16px"
              fontWeight="400"
              justifyContent="space-between"
              paddingRight="40px"
              marginBottom="24px"
              fontStyle="normal"
              defaultValue={createMode}
              onChange={(value: ECreateMode) => setCreateMode(value)}
            >
              <Radio marginRight={16} value={ECreateMode.CREATE}>
                Create step from scratch
              </Radio>
              {commonLibraryStore?.isFeatureEnabled && (
                <Radio value={ECreateMode.EXISTING}>Select step from common library</Radio>
              )}
            </RadioGroup>

            {createMode === ECreateMode.CREATE && (
              <>
                <Label className={styles.label}>Step Name</Label>
                <Input
                  type="text"
                  className={styles.field}
                  value={newTitle}
                  onChange={event => setNewTitle(event.target.value)}
                  _focus={{ borderColor: currentTheme?.primaryColor ?? primary }}
                />
              </>
            )}

            {createMode === ECreateMode.EXISTING && (
              <>
                <InputGroup borderRadius="6px" background="white">
                  <InputLeftElement pointerEvents="none">
                    <Search2Icon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    type="search"
                    placeholder="Search..."
                    value={keyword}
                    onChange={event => setKeyword(event.target.value)}
                    _focus={{ borderColor: currentTheme?.primaryColor ?? primary }}
                  />
                </InputGroup>
                <SelectStepPanel
                  selectedOption={selectedCommonStepId}
                  setSelectedOption={setSelectedCommonStepId}
                  data={getValidArray(commonLibraryStore?.commonLibraryComponents).filter(
                    commonStep => !stepIdsToExclude.includes(commonStep?.stepId)
                  )}
                />
              </>
            )}
          </FormGroup>
          <FormGroup>
            <Label className={styles.label}>Step Position</Label>
            <div className={styles.selectWrapper}>
              <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                <DropdownToggle className={styles.selectButton} nav>
                  <span>{stepPositionName}</span>
                  <Icon icon="angle-down" group="unicon" />
                </DropdownToggle>
                <DropdownMenu className={styles.showOption}>
                  <DropdownItem
                    onClick={() => {
                      setStepPosition(StepPosition.PrependToTheBeginningOfList)
                      setStepPositionName(StepPositionNames.PrependToTheBeginningOfList)
                    }}
                  >
                    <Label>{StepPositionNames.PrependToTheBeginningOfList}</Label>
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      setStepPosition(StepPosition.AppendToTheEndOfList)
                      setStepPositionName(StepPositionNames.AppendToTheEndOfList)
                    }}
                  >
                    <Label>{StepPositionNames.AppendToTheEndOfList}</Label>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
              <Label className={styles.smallLabel}>You can drag/drop step to change its position later</Label>
            </div>
          </FormGroup>
        </ModalBody>

        <ModalFooter borderTop="1px solid #E2E8F0">
          <Button
            variant="outline"
            background="white"
            border="1px solid #E2E8F0"
            borderRadius="6px"
            color="gray.700"
            fontWeight={500}
            fontSize="16px"
            lineHeight="24px"
            _hover={{ background: 'whiteAlpha.700' }}
            _active={{ background: 'whiteAlpha.700' }}
            onClick={onClose}
            marginRight="16px"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            borderRadius="6px"
            color="white"
            fontWeight={500}
            fontSize="16px"
            lineHeight="24px"
            className={styles.saveButton}
            background={currentTheme?.primaryColor ?? 'primary.500'}
            _hover={{
              background: currentTheme?.primaryColor ?? 'primary.700',
              opacity: currentTheme?.primaryColor ? 0.8 : 1
            }}
            _active={{
              background: currentTheme?.primaryColor ?? 'primary.700',
              opacity: currentTheme?.primaryColor ? 0.8 : 1
            }}
            onClick={() => handleCreateStep()}
            disabled={loading}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
export default observer(ModalAddStep)
